import React, { useEffect, useRef, useState } from "react";
import { Modal, Button, Form, Dropdown } from "react-bootstrap";
import "./RejectPlanModal.css";
import appURL from "../../../appURL.json";
import { transformRejectWorkplanReasons } from "../PlanningFactoryData/PlanningFactoryData";
import { getAll, syncDataToLocal } from "../../../localDB";
import TauriLoader2 from "../../TauriLoader2/TauriLoader2";
import OpalAlertModal from "../../OpalAlertModal/OpalAlertModal";
import { useNavigate } from "react-router-dom";

const RejectPlanModal = ({ show, onHide, worksheet }) => {
  const [selectedReason, setSelectedReason] = useState(null);
  const [reasonList, setReasonList] = useState([]);
  const [reasonError, setReasonError] = useState(false);
  const [remarksError, setRemarksError] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);
  const [isLoadingReasons, setIsLoadingReasons] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const remarksRef = useRef(null);
  const navigate = useNavigate();

  const fetchOnlineRejectWorkplanReasons = async () => {
    try {
      const token = localStorage.getItem("token");
      const devId = localStorage.getItem("devId");

      const response = await fetch(`${appURL.API_Worksheet}/reject/reasons`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          "X-ETR-Device-ID": devId,
        },
      });

      if (!response.ok) {
        console.log("Failed to fetch reject reasons!");
        return;
      }

      const data = await response.json();

      const transformRejectReasons = await transformRejectWorkplanReasons(
        data.reasons,
      );

      if (transformRejectReasons) {
        await syncDataToLocal(
          "RejectWorkplanReasons",
          transformRejectReasons,
        );
      }

      setReasonList(transformRejectReasons || []);
    } catch (err) {
      console.error("Fetch Online Reject Reasons Error:", err);
    }
  };

  const fetchOfflineRejectWorkplanReasons = async () => {
    try {
      const rejectReasons = await getAll("RejectWorkplanReasons");
      setReasonList(rejectReasons || []);
    } catch (err) {
      console.error("Fetch Offline Reject Reasons Error:", err);
    }
  };

  const fetchData = async () => {
    try {
      const isOnline = JSON.parse(localStorage.getItem("isOnline"));
      const hasInternet = navigator.onLine;

      setIsLoadingReasons(true);

      if (isOnline && hasInternet) {
        await fetchOnlineRejectWorkplanReasons();
      } else {
        await fetchOfflineRejectWorkplanReasons();
      }
    } finally {
      setIsLoadingReasons(false);
    }
  };

  const handleReject = () => {
    const remarks = remarksRef.current?.value.trim() || "";

    let hasError = false;

    if (!selectedReason) {
      setReasonError(true);
      hasError = true;
    } else {
      setReasonError(false);
    }

    if (!remarks) {
      setRemarksError(true);
      hasError = true;
    } else {
      setRemarksError(false);
    }

    if (hasError) {
      return;
    }

    setShowConfirmModal(true);
  };

  const confirmReject = async () => {
    setShowConfirmModal(false);
    setIsRejecting(true);

    try {
      const remarks = remarksRef.current?.value.trim() || "";
      const token = localStorage.getItem("token");
      const devId = localStorage.getItem("devId");

      const payload = {
        ReasonID: selectedReason.ID,
        Remarks: remarks,
      };

      const response = await fetch(
        `${appURL.API_Worksheet}/${worksheet.ID}/reject`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
            "X-ETR-Device-ID": devId,
          },
          body: JSON.stringify(payload),
        },
      );

      if (!response.ok) {
        console.log("Failed to reject worksheet!");
        return;
      }

      onHide();
      navigate("/planning");
    } catch (err) {
      console.error("Reject Error:", err);
    } finally {
      setSelectedReason(null);
      setReasonError(false);
      setRemarksError(false);
      setIsRejecting(false);

      if (remarksRef.current) {
        remarksRef.current.value = "";
      }
    }
  };

  useEffect(() => {
    if (show) {
      fetchData();
      setReasonError(false);
      setRemarksError(false);
      setSelectedReason(null);
      setShowConfirmModal(false);
      setIsRejecting(false);

      if (remarksRef.current) {
        remarksRef.current.value = "";
      }
    }
  }, [show]);

  return (
    <>
      <Modal
        className="reject-plan-modal"
        show={show}
        onHide={isRejecting ? undefined : onHide}
        size="lg"
        centered
        backdrop={isRejecting ? "static" : true}
        keyboard={!isRejecting}
      >
        <Modal.Header>Disappove Plan</Modal.Header>

        <Modal.Body>
          <Form.Group className="mt-3">
            <Form.Label>
              Reason <span className="text-danger">*</span>
            </Form.Label>

            <Dropdown>
              <Dropdown.Toggle
                className={`form-control text-start d-flex align-items-center ${
                  reasonError ? "is-invalid" : ""
                }`}
                disabled={isRejecting || isLoadingReasons}
              >
                {isLoadingReasons ? (
                  <span className="ms-2">Loading disapprove reasons...</span>
                ) : selectedReason ? (
                  <span>
                    <span className="text-code me-1">
                      {selectedReason.Code}
                    </span>
                    <span>{selectedReason.Description}</span>
                  </span>
                ) : (
                  "-- Select Reason --"
                )}
              </Dropdown.Toggle>

              <Dropdown.Menu className="w-100">
                {reasonList.map((reason) => (
                  <Dropdown.Item
                    key={reason.Code}
                    onClick={() => {
                      setSelectedReason(reason);
                      setReasonError(false);
                    }}
                  >
                    <span className="text-code me-1">{reason.Code}</span>
                    <span>{reason.Description}</span>
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>
          </Form.Group>

          <Form.Group className="m-0 my-2 w-100">
            <Form.Label>
              Remarks <span className="text-danger">*</span>
            </Form.Label>

            {remarksError && (
              <Form.Control.Feedback type="invalid">
                Remarks is required.
              </Form.Control.Feedback>
            )}
            
            <Form.Control
              as="textarea"
              rows={3}
              ref={remarksRef}
              disabled={isRejecting}
              maxLength={255}
              className={remarksError ? "is-invalid" : ""}
              onChange={() => {
                if (remarksRef.current?.value.trim()) {
                  setRemarksError(false);
                }
              }}
            />
          </Form.Group>
        </Modal.Body>

        <Modal.Footer className="justify-content-end">
          <Button
            className="btn-reject d-flex px-5 gap-2"
            disabled={isRejecting}
            onClick={handleReject}
          >
            {isRejecting && (
              <TauriLoader2
                width="1.2rem"
                height="1.2rem"
                borderwidth="0.2rem"
                color="#000"
              />
            )}

            <span className="text-nowrap">
              {isRejecting ? "Disapproving..." : "Disapprove"}
            </span>
          </Button>
        </Modal.Footer>
      </Modal>

      <OpalAlertModal
        show={showConfirmModal}
        onHide={() => setShowConfirmModal(false)}
        title="Confirm Disapproval"
        bodyText="Are you sure you want to disapprove this plan?"
        confirmButtonText="Yes"
        cancelButtonText="No"
        modalType="confirm"
        onConfirm={confirmReject}
      />
    </>
  );
};

export default RejectPlanModal;